import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { useContextTree } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { useEffect } from 'react';

/** Org → Workspace → App cascade, plus Environment as its own control. Environment is not a
 *  preference: it is the publish target and the scope of version history, so it gets a segmented
 *  control in the bar rather than a fourth dropdown nobody reads (the Vercel model). */
export function ContextSwitcher(): React.JSX.Element {
  const { data: orgs, isLoading } = useContextTree();
  const ctx = useContextStore();

  const org = orgs?.find((o) => o.id === ctx.orgId) ?? orgs?.[0];
  const workspace = org?.workspaces.find((w) => w.id === ctx.workspaceId) ?? org?.workspaces[0];
  const app = workspace?.apps.find((a) => a.id === ctx.appId) ?? workspace?.apps[0];
  const environment = app?.environments.find((e) => e.id === ctx.environmentId) ?? app?.environments[0];

  // Backfill the store from the tree. The store starts empty (and can hold a stale id after a
  // resource is deleted elsewhere), so whatever the cascade above actually resolved to becomes the
  // truth. Without this every page's `enabled: Boolean(appId)` query would sit disabled forever.
  const { setOrg, setWorkspace, setApp, setEnvironment } = ctx;
  useEffect(() => {
    if (org && org.id !== ctx.orgId) setOrg(org.id);
    if (workspace && workspace.id !== ctx.workspaceId) setWorkspace(workspace.id);
    if (app && app.id !== ctx.appId) setApp(app.id);
    if (environment && environment.id !== ctx.environmentId) setEnvironment(environment.id);
  }, [org, workspace, app, environment, ctx, setOrg, setWorkspace, setApp, setEnvironment]);

  if (isLoading) return <Skeleton className="h-7 w-64" />;
  if (!org) return <span className="text-sm text-muted-foreground">No organization</span>;

  return (
    <div className="flex items-center gap-2">
      <Select value={org.id} onValueChange={setOrg}>
        <SelectTrigger className="h-7 border-none shadow-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {orgs?.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {app && workspace && (
        <>
          <span className="text-muted-foreground">/</span>
          <Select value={app.id} onValueChange={setApp}>
            <SelectTrigger className="h-7 border-none shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {workspace.apps.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {app && app.environments.length > 0 && (
        <div className="ml-2 flex items-center rounded-lg border p-0.5">
          {app.environments.map((env) => (
            <button
              key={env.id}
              type="button"
              onClick={() => setEnvironment(env.id)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs capitalize transition-colors',
                env.id === environment?.id
                  ? 'bg-secondary font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {env.name}
              {/* The kill switch is on. Surfacing it in the bar means an author cannot wonder for an
                  hour why nothing is reaching the device. */}
              {!env.sdk_enabled && <span className="ml-1.5 text-destructive">off</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
